import * as React from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid2,
  IconButton,
  Collapse,
} from "@mui/material";
import axios from "axios";
import styles from "./validateBankAc.module.css";
import CloseIcon from "@mui/icons-material/Close";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import { useNavigationType } from "react-router-dom";
import { useEffect } from "react";
import store from "../../../Store/Store";
import config from "../../../config/config";
import { jwtDecode } from "jwt-decode";
import {
  DataGrid,
  useGridApiRef,
  GridPreferencePanelsValue,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import Tooltip from "@mui/material/Tooltip";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";

/* ------------------------------------------------------------------
 * Typography tokens.
 * These MUST live at module scope: DataTable is also defined at module
 * scope, so constants declared inside ValidateBankAc are invisible to
 * it. --font-source-sans-3 is declared on :root in global.css.
 * ------------------------------------------------------------------ */
const SANSOURCE = "var(--font-source-sans-3), sans-serif";

// Column header colour, matching --color-dimgray-200.
const HEADER_COLOR = "#666666";
// Body / cell text colour, matching --color-dimgray-300.
const TEXT_COLOR = "#4C4E64";
// Accent used for checked states and the panel's text buttons.
const ACCENT_COLOR = "#3246A9";
const MUTED_COLOR = "#8C8C8C";

// Text columns get exactly one operator, so the operator dropdown can be
// hidden and the panel shows nothing but a text box.
const CONTAINS_ONLY = getGridStringOperators().filter(
  (operator) => operator.value === "contains",
);

// Single-select columns get "is", which renders a value dropdown instead.
const IS_ONLY = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is",
);

// Same parser the built-in GridToolbarQuickFilter uses.
const parseQuickFilter = (input) =>
  input.split(" ").filter((word) => word !== "");

const EMPTY_FILTER_MODEL = {
  items: [],
  quickFilterValues: [],
  quickFilterExcludeHiddenColumns: false,
};

// Fixed width for the filter/columns popper. The form inside stacks to fit
// this, so the panel never depends on how wide the table happens to be.
const PANEL_WIDTH = 320;

/* ------------------------------------------------------------------
 * Panel typography + shape.
 * Both the columns panel and the filter panel render through the
 * `panel` slot, which is a Popper portaled to <body>. Nothing in the
 * grid's own sx reaches them, so the font, colour and corner radius
 * have to be re-stated here. GridPanel wraps both, so this one block
 * covers the columns list and the filter form alike.
 *
 * Note: only ONE key per selector — a repeated key in an object literal
 * silently overwrites the earlier one, which is how the panelWrapper
 * font-family went missing before.
 * ------------------------------------------------------------------ */
const PANEL_SX = {
  // catch-all for nodes MUI doesn't expose a stable class for
  "& , & *": {
    fontFamily: SANSOURCE,
  },

  // the white card itself. overflow:hidden is what actually clips the
  // footer row and the scrollbar to the rounded corners.
  // .MuiPaper-root is MUI X v6 and earlier, .MuiDataGrid-paper is v7+.
  "& .MuiPaper-root, & .MuiDataGrid-paper": {
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },
  "& .MuiDataGrid-panelWrapper": {
    fontFamily: SANSOURCE,
    borderRadius: "var(--br-xs)",
    overflow: "hidden",
  },

  // column rows in the columns panel
  "& .MuiFormControlLabel-label, & .MuiTypography-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  // the panel's own search box and the filter form inputs
  "& .MuiInputBase-root, & .MuiInputBase-input, & .MuiFormLabel-root, & .MuiSelect-select":
    {
      fontFamily: SANSOURCE,
      fontSize: 14,
      color: TEXT_COLOR,
    },
  "& .MuiInputBase-input::placeholder": {
    fontFamily: SANSOURCE,
    color: MUTED_COLOR,
    opacity: 1,
  },
  // focused label + outline, otherwise MUI's default primary blue
  "& .MuiFormLabel-root.Mui-focused": { color: ACCENT_COLOR },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT_COLOR,
  },
  // Show/Hide All + RESET footer
  "& .MuiDataGrid-panelFooter .MuiButton-root": {
    fontFamily: SANSOURCE,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    color: ACCENT_COLOR,
  },
  // checkboxes, otherwise MUI's default blue
  "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate":
    {
      color: ACCENT_COLOR,
    },
};

/* ------------------------------------------------------------------
 * Select menus.
 * The Column and Value dropdowns in the filter panel are MUI Selects,
 * and a Select opens its menu in its own Popover portaled to <body> —
 * a sibling of the GridPanel, not a child. PANEL_SX (including the
 * "& , & *" catch-all) never reaches it, so the options fall back to
 * theme-default Roboto / rgba(0,0,0,0.87). Restate them here.
 * ------------------------------------------------------------------ */
const SELECT_MENU_SX = {
  borderRadius: "var(--br-xs)",
  border: "1px solid #E5E7EB",
  boxShadow: "0 8px 24px rgba(76, 78, 100, 0.12)",
  "& .MuiMenuItem-root": {
    fontFamily: SANSOURCE,
    fontSize: 14,
    fontWeight: 500,
    color: TEXT_COLOR,
    letterSpacing: 0,
    textTransform: "none",
  },
  "& .MuiMenuItem-root.Mui-selected": {
    color: ACCENT_COLOR,
    backgroundColor: "rgba(50, 70, 169, 0.08)",
  },
  "& .MuiMenuItem-root.Mui-selected:hover": {
    backgroundColor: "rgba(50, 70, 169, 0.12)",
  },
};

export default function ValidateBankAc({ companyId }) {
  const [WalletInfo, setWalletinfo] = useState("Loading...");
  const [bankName, setBankName] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [kycCompanyId, setKycCompanyId] = useState(companyId);
  const [validatedRows, setValidatedRows] = useState([]);
  const [statusRows, setStatusRows] = useState([]); // [{date, amount, sourceName, accountNumber, status, reason}]
  const [sPage, setSPage] = useState(0);
  const [sRpp, setSRpp] = useState(5);
  const [showForm, setShowForm] = useState(false);
  const [open, setOpen] = useState(true);
  const navType = useNavigationType();
  const [submitting, setSubmitting] = React.useState(false);
  const [submittedOpen, setSubmittedOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [companyTempId, setCompanyTempId] = useState("");
  const [errors, setErrors] = useState({
    bankName: "",
    ifsc: "",
    accountNumber: "",
  });
  useEffect(() => {
    setKycCompanyId(store.KycCompanyId ? store.KycCompanyId : companyId);
  }, []);
  // validated source accounts
  const [vPage, setVPage] = useState(0);
  const [vRpp, setVRpp] = useState(5);

  // money load accepted
  const [aPage, setAPage] = useState(0);
  const [aRpp, setARpp] = useState(5);

  // money load rejected
  const [rPage, setRPage] = useState(0);
  const [rRpp, setRRpp] = useState(5);

  const [whitelistingErr, setWhitelistingErr] = useState("");
  const [latest, setLatest] = useState(null);
  const [rejectedLoad, setRejectedLoad] = useState(null);
  const [acceptedLoad, setAcceptedLoad] = useState(null);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  let tokens = localStorage.getItem("authToken");
  let parsedTokens = JSON.parse(tokens);
  const BASE_URL = "http://localhost:5050";

  useEffect(() => {
    setCompanyTempId(kycCompanyId);
  }, [store]);

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);

      if (decoded && decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
        return decoded.exp < currentTime;
      }
    } catch (error) {
      console.log("Error decoding token:", error);
      return true; // Treat decoding failures as expired tokens
    }
    return true;
  };

  const getNewTokens = async (authData) => {
    if (!authData?.RefreshToken) {
      localStorage.removeItem("authToken");
      navigate("/login");
      return;
    }

    try {
      const client = new CognitoIdentityProviderClient({
        region: "ap-south-1",
      });

      const params = {
        ClientId: config.Client_Id,
        AuthFlow: "REFRESH_TOKEN_AUTH",
        AuthParameters: {
          REFRESH_TOKEN: authData?.RefreshToken,
        },
      };

      const command = new InitiateAuthCommand(params);
      const refreshResponse = await client.send(command);

      if (refreshResponse.AuthenticationResult) {
        const newAuthToken = {
          AccessToken: refreshResponse.AuthenticationResult.AccessToken,
          RefreshToken: authData?.RefreshToken, // Keep old refresh token
          IdToken: refreshResponse.AuthenticationResult.IdToken,
        };

        localStorage.setItem("authToken", JSON.stringify(newAuthToken));
        if (store.refresh) {
          store.setRefresh(false);
        } else {
          store.setRefresh(true);
        }
      } else {
        localStorage.removeItem("authToken");
        navigate("/login");
      }
    } catch (error) {
      console.log("Error refreshing tokens:", error);
      localStorage.removeItem("authToken");
      navigate("/login");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        let tokens = localStorage.getItem("authToken");
        let parsedTokens = JSON.parse(tokens);
        const res = await fetch(
          `${config.kyc_api_endpoint}/basic-kyc/by-company`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              aws_cognito_id_token: parsedTokens.IdToken,
              aws_cognito_access_token: parsedTokens.AccessToken,
            },
            body: JSON.stringify({ company_id: kycCompanyId }),
          },
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        setCompanyName(data.data.Company_Name);
      } catch (error) {
        console.log("Error fetching KYC data:", error);
      }
    };

    if (kycCompanyId) {
      fetchData();
    }
  }, [kycCompanyId, config.kyc_api_endpoint]);

  useEffect(() => {
    let auth = localStorage.getItem("authToken");
    if (!auth) {
      navigate("/login");
      return;
    }
    let parsedTokens = JSON.parse(auth);
    const expired = isTokenExpired(parsedTokens.IdToken);
    if (expired) {
      parsedTokens = getNewTokens(parsedTokens);
    }
    let payload = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${config.node_api_endpoint}/cupi/admin/get_rejected_inwards`,
      headers: {
        aws_cognito_id_token: parsedTokens.IdToken,
        aws_cognito_access_token: parsedTokens.AccessToken,
        "Content-Type": "application/json",
      },
      data: {
        ledger_label: store.Company_Id,
        last_no_of_days: 180,
      },
    };

    axios
      .request(payload)
      .then((res) => {
        setRejectedLoad(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [store.Company_Id]);

  useEffect(() => {
    let auth = localStorage.getItem("authToken");
    if (!auth) {
      navigate("/login");
      return;
    }

    let payload = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${config.node_api_endpoint}/cupi/admin/get_credited_inwards`,
      headers: {
        aws_cognito_id_token: parsedTokens.IdToken,
        aws_cognito_access_token: parsedTokens.AccessToken,
        "Content-Type": "application/json",
      },
      data: {
        ledger_label: store.Company_Id,
        last_no_of_days: 180,
      },
    };

    axios
      .request(payload)
      .then((res) => {
        setAcceptedLoad(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [store.Company_Id]);

  useEffect(() => {
    let auth = localStorage.getItem("authToken");
    if (!auth) {
      navigate("/login");
      return;
    }
    let parsedTokens = JSON.parse(auth);
    const expired = isTokenExpired(parsedTokens.IdToken);
    if (expired) {
      parsedTokens = getNewTokens(parsedTokens);
    }

    let payload = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${config.node_api_endpoint}/cupi/admin/get_white_listed_accounts`,
      headers: {
        aws_cognito_id_token: parsedTokens.IdToken,
        aws_cognito_access_token: parsedTokens.AccessToken,
        "Content-Type": "application/json",
      },
      data: {
        ledger_label: store.Company_Id,
      },
    };

    axios
      .request(payload)
      .then((res) => {
        setLatest(res.data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [store.Company_Id]);

  useEffect(() => {
    if (!tokens) {
      console.log("no auth");
      navigate("/login");
      return;
    } else {
    }
    try {
      const expired = isTokenExpired(parsedTokens.IdToken);
      if (expired) {
        parsedTokens = getNewTokens(parsedTokens);
      }
      let payload = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${config.node_api_endpoint}/cupi/admin/get_cupi_company_detail`,
        headers: {
          aws_cognito_id_token: parsedTokens.IdToken,
          aws_cognito_access_token: parsedTokens.AccessToken,
          "Content-Type": "application/json",
        },
        data: {
          ledger_label: store.Company_Id,
        },
      };

      axios
        .request(payload)
        .then((res) => {
          setWalletinfo(res.data.data);
        })
        .catch((er) => {
          console.log("error-->", er);
        });
    } catch (err) {
      console.log("Error in walletInfo", err);
    }
  }, []);

  const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/; // length 11, ex: UTIB0XXXXX
  const ACC_REGEX = /^[0-9]{6,20}$/;
  function validateField(name, value) {
    let msg = "";
    if (name === "bankName") {
      if (!value) msg = "Bank name is required";
      else if (value.length < 2) msg = "Enter a valid bank name";
    } else if (name === "ifsc") {
      if (!value) msg = "IFSC is required";
      else if (!IFSC_REGEX.test(value))
        msg = "Enter a valid IFSC (e.g., UTIB0XXXXX)";
    } else if (name === "accountNumber") {
      if (!value) msg = "Account number is required";
      else if (!ACC_REGEX.test(value)) msg = "6–20 digits only";
    }
    setErrors((p) => ({ ...p, [name]: msg }));
    return !msg;
  }

  function validateAll() {
    // const ok1 = validateField("bankName", bankName);
    const ok2 = validateField("ifsc", ifsc);
    const ok3 = validateField("accountNumber", accountNumber);
    return /* ok1 && */ ok2 && ok3;
  }

  // const toggle = () => setOpen((s) => !s);
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      // toggle();
    }
  };
  const handleSubmit = async () => {
    try {
      setWhitelistingErr("");
      setSubmitting(true);
      setSubmitError("");

      const IFSC = (ifsc || "").trim().toUpperCase();
      const ACC_NO = (accountNumber || "").trim();

      const payload = {
        ifsc: IFSC,
        acc_no: ACC_NO,
        company_id: kycCompanyId,
        company_name: companyName,
        ledger_label: store.Company_Id,
      };

      let tokens = localStorage.getItem("authToken");
      let parsedTokens = JSON.parse(tokens);
      const res = await axios
        .post(`${config.kyc_api_endpoint}/bank-account-verify`, payload, {
          // timeout: 15000,
          headers: {
            aws_cognito_id_token: parsedTokens.IdToken,
            aws_cognito_access_token: parsedTokens.AccessToken,
          },
          validateStatus: () => true,
        })
        .then(async (res) => {
          if (res.data.verified == false || res.data.status == "unverified") {
            setWhitelistingErr(
              "We couldn’t verify your bank account. Please double-check your IFSC and account number and try again.",
            );
          }

          if (res.data.status == "timeout") {
            setWhitelistingErr(
              "The verification took too long. Please re-enter your account details and try again.",
            );
          }
          if (res.data.status == "duplicate") {
            setWhitelistingErr(res.data.message + "");
          }
          if (res.data.status === "approved") {
            let whitelistResponse;

            try {
              whitelistResponse = await axios.post(
                `${config.node_api_endpoint}/cupi/admin/add_whitelisting_to_master_account`,
                {
                  ledger_label: store.Company_Id,
                  bank_account_no: ACC_NO,
                  bank_ifsc: IFSC,
                  account_name: res.data.beneficiary_name,
                },
                {
                  headers: {
                    aws_cognito_id_token: parsedTokens.IdToken,
                    aws_cognito_access_token: parsedTokens.AccessToken,
                    "Content-Type": "application/json",
                  },
                  timeout: 15000,
                  validateStatus: () => true,
                },
              );

              if (whitelistResponse.data.status === 200) {
                setSubmittedOpen(true);
                setRefresh(!refresh);
                setBankName("");
                setIfsc("");
                setAccountNumber("");
                return;
              }
            } catch (err) {
              console.log("Error calling whitelist API:", err.message);
            }
          }
          if (res.data.verified && res.status === 200) {
            setSubmittedOpen(true);
            setRefresh(!refresh);
            setBankName("");
            setIfsc("");
            setAccountNumber("");
            return;
          }
          setRefresh(!refresh);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (err) {
      console.log("error-->", err);
    } finally {
      setSubmitting(false);
    }
  };

  function toValidatedRows(list) {
    const arr = Array.isArray(list) ? list : [];
    return arr
      .slice()
      .sort(
        (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0),
      )
      .map((r) => ({
        date: r?.created_at ? new Date(r.created_at).toLocaleDateString() : "—",
        accountNumber: r?.bank_account ?? "—",
        ifsc: r?.full_ifsc ?? "—",
        beneficiary_name: r?.account_name ?? "—",
      }));
  }

  function toValidatedRowsRejected(list) {
    const arr = Array.isArray(list) ? list : [];
    return arr
      .slice()
      .sort(
        (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0),
      )
      .map((r) => ({
        date: r?.inward_timestamp ?? "—",
        accountNumber: r?.sender_bank_account_no ?? "—",
        sourceAccountName: r?.sender_bank_account_name ?? "—",
        amount: r?.amount ?? "—",
        status: r?.status ?? "Rejected",
        reason:
          r?.reason ?? `Source A/c name and Company Bank A/c name don’t match.`,
        ifsc: r?.ifsc ?? "—",
        utr: r?.utr ?? "-",
      }));
  }

  const prettySvcStatus = (code = "") => {
    const map = {
      FUNDS_RECEIVED_CREDIT_ALLOCATION_PENDING:
        "Funds received — wallet load in progress.",
      FUNDS_RECEIVED_CREDIT_PASSED: "Wallet loaded successfully.",
    };

    // Fallback: prettify unknown enum codes like "SOME_STATUS" -> "Some status"
    if (map[code]) return map[code];
    return String(code)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());
  };
  function toValidatedRowsAccepted(list) {
    const arr = Array.isArray(list) ? list : [];

    return arr
      .slice()
      .sort(
        (a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0),
      )
      .map((r) => ({
        date: r?.timestamp_when_credit_given_to_corporate_wallet,
        amount: r?.amount ?? "—",
        sourceAccountName: r?.sender_name ?? "—",
        accountNumber: r?.sender_account_no ?? "—",
        ifsc: r?.sender_ifsc ?? "—",
        status: prettySvcStatus(r?.svc_load_status),
        utr: r?.utr_visible_to_client ?? "-",
        reason: r?.reason ?? "Source A/c name and Company Bank A/c name match.",
      }));
  }

  const formValid =
    !errors.bankName &&
    !errors.ifsc &&
    !errors.accountNumber &&
    bankName &&
    ifsc &&
    accountNumber;

  return (
    <>
      <Dialog
        open={submittedOpen}
        onClose={() => setSubmittedOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "var(--br-xs)",
              fontFamily: SANSOURCE,
              "& *": { fontFamily: SANSOURCE },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          Details Submitted
          <IconButton
            aria-label="close"
            onClick={() => setSubmittedOpen(false)}
            edge="end"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            pb: 3, // add some bottom padding since we removed DialogActions
          }}
        >
          <Typography align="center" sx={{ color: "text.secondary" }}>
            Once validated, the bank account details will show up in the list
            below.
          </Typography>
        </DialogContent>
      </Dialog>

      <Box sx={{ p: { xs: 2, md: 3 }, /* maxWidth: 1100, */ mx: "auto" }}>
        <Box
          role="button"
          tabIndex={0}
          // onClick={toggle}
          onKeyDown={onKey}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: "calc(100vw - 330px)",
            gap: 1,
            mb: 1,
            cursor: "pointer",
          }}
          aria-expanded={open}
          aria-controls="load-account-details"
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              letterSpacing: 0.2,
              position: "relative",
              pb: 1,
              display: "inline-block",
              width: "max-content",
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0, // underline matches text width
                bottom: 0,
                height: 2,
                bgcolor: "divider",
              },
            }}
          >
            Load Account Details (Transfer money to this account)
          </Typography>
        </Box>

        {/* Collapsible content */}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            id="load-account-details"
            sx={{
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 0.5,
                lineHeight: 1.2,
                fontWeight: "bold",
                fontSize: "20px",
                fontFamily: SANSOURCE,
                display: "flex",
                alignItems: "start",
                justifyContent: "start",
                gap: "8px",
                color: TEXT_COLOR,
              }}
            >
              <img
                alt={"image"}
                src={"/alert-circle.svg"}
                style={{ height: "24px", width: "24px" }}
              />{" "}
              Transfer money from {store.companyName} /{" "}
              {store?.tradeName ? store?.tradeName : store?.companyName} bank
              account only. Only IMPS, NEFT, RTGS, FT loads are accepted. UPI is
              not accepted.
            </Typography>
            {/* Bank details in aligned rows */}
            <Grid2 spacing={0.5} sx={{ mb: 2, mt: 2 }}>
              <DetailRow label="Ac.name" value={store.companyName} />
              <DetailRow
                label="Ac.no"
                value={WalletInfo?.van?.acc_no || "Loading..."}
              />
              <DetailRow
                label="IFSC"
                value={WalletInfo?.van?.ifsc || "Loading..."}
              />
              <DetailRow label="Branch" value="CC HUB" />
              <DetailRow label="Bank name" value="Axis Bank" />
            </Grid2>
          </Box>
        </Collapse>
        {/* Notes paragraph + bullets */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              lineHeight: 1.8,
              fontWeight: "bold",
              color: TEXT_COLOR,
            }}
          >
            Please avoid loading money between 11:00 pm and 1:00 am as Bank
            servers are undergo End of Day processes and are unreliable during
            this time.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              lineHeight: 1.8,
              fontWeight: "bold",
              color: TEXT_COLOR,
            }}
          >
            After we receive the funds, your wallet balance may update within 30
            minutes to 2 hours due to batch processing
          </Typography>

          <Box
            component="ul"
            sx={{ m: 0, pl: 3, color: "text.secondary", listStyleType: "disc" }}
          >
            <li>
              <Typography variant="body2">
                NEFT and RTGS transfers may be delayed if you send them during
                bank holidays or outside bank working hours.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                In those cases, the money will show up on the next working day.
              </Typography>
            </li>
          </Box>
        </Box>
        {/* How it works */}
        <Box sx={{ mb: 3, mt: 3, color: TEXT_COLOR }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            How it works:
          </Typography>

          <Box component="ol" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
              You can only add money from your{" "}
              <strong>
                {store.companyName} /{" "}
                {store.tradeName ? store.tradeName : store.companyName}
              </strong>{" "}
              bank account.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
              If you use a different account, the transfer will be rejected. So,
              it’s best to stick with your company account.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1.5 }}>
              We automatically check where the money is coming from, so you try
              to transfer Rs.1 to see if it gets loaded and if it does not, then
              try <strong>step 4.</strong>
            </Typography>
            <Typography component="li" variant="body1">
              You can also add and verify your bank account details below to
              make sure everything works perfectly when you transfer.
            </Typography>
          </Box>
        </Box>
        <div style={{ marginTop: "15px" }}></div>
        <div className={styles.split}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            Add and verify your company account details
          </Typography>
          {!store.isUserViewAdmin && !store.isUserGlobalViewAdmin ? (
            <Button
              variant="contained"
              size="medium"
              sx={{
                bgcolor: "#2348AB",
                "&:hover": { bgcolor: "#3f55c7" },
                textTransform: "none",
                borderRadius: 2,
                px: 5,
                fontWeight: 700,
              }}
              onClick={() => setShowForm(!showForm)}
            >
              Add
            </Button>
          ) : (
            <Button
              variant="contained"
              size="medium"
              sx={{
                bgcolor: "#2348AB",
                "&:hover": { bgcolor: "#3f55c7" },
                textTransform: "none",
                borderRadius: 2,
                px: 5,
                fontWeight: 700,
                opacity: 0.45,
                cursor: "default",
              }}
            >
              Add
            </Button>
          )}
        </div>

        {showForm && (
          <Grid2
            container
            alignItems="center"
            justifyContent="space-between"
            rowSpacing={2}
            columnSpacing={2}
            sx={{ mb: 2, mt: 1 }}
          >
            <Grid2
              xs={12}
              md="auto"
              sx={{ flex: { md: "1 1 32%" }, minWidth: { md: 260 } }}
            >
              {" "}
              <TextField
                fullWidth
                label="Account number"
                value={accountNumber}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 20);
                  setAccountNumber(v);
                  if (errors.accountNumber) validateField("accountNumber", v);
                }}
                onBlur={() => validateField("accountNumber", accountNumber)}
                error={Boolean(errors.accountNumber)}
                helperText={errors.accountNumber || " "}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 20,
                }}
                sx={{
                  "& .MuiInputBase-input, & .MuiFormLabel-root, & .MuiFormHelperText-root":
                    { fontFamily: SANSOURCE },
                }}
              />
            </Grid2>

            {/* Account number (digits only) + Submit */}
            <Grid2
              xs={12}
              md="auto"
              sx={{
                flex: { md: "1 1 32%" },
                minWidth: { md: 320 },
                display: "flex",
                gap: 2,
              }}
            >
              <TextField
                fullWidth
                label="IFSC"
                value={ifsc}
                onChange={(e) => {
                  const v = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, "")
                    .slice(0, 11);
                  setIfsc(v);
                  if (errors.ifsc) validateField("ifsc", v);
                }}
                onBlur={() => validateField("ifsc", ifsc)}
                error={Boolean(errors.ifsc)}
                helperText={errors.ifsc || " "}
                inputProps={{ maxLength: 11 }}
                sx={{
                  "& input": { textTransform: "uppercase", letterSpacing: 1 },
                  "& .MuiInputBase-input, & .MuiFormLabel-root, & .MuiFormHelperText-root":
                    { fontFamily: SANSOURCE },
                }}
              />

              <Button
                variant="contained"
                onClick={async () => {
                  if (!validateAll()) return;
                  await handleSubmit(); // your existing submit
                }}
                sx={{
                  px: 3,
                  borderRadius: 2,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  backgroundColor: "#2348AB",
                  height: "55px",
                }}
              >
                Submit
              </Button>
            </Grid2>
          </Grid2>
        )}

        {whitelistingErr && (
          <span
            style={{
              color: "#B20A00",
              fontSize: "20px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontFamily: SANSOURCE,
            }}
          >
            {whitelistingErr}
          </span>
        )}
        {!whitelistingErr && submitError && (
          <span
            style={{
              color: "#B20A00",
              fontSize: "20px",
              marginBottom: "10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontFamily: SANSOURCE,
            }}
          >
            {submitError}
          </span>
        )}

        <Typography
          variant="body2"
          sx={{
            mb: 1,
            fontSize: "24px",
            color: TEXT_COLOR,
            paddingTop: "5px",
          }}
        >
          Below is the list of validated and whitelisted source account(s) from
          which you can load your CUPI wallet.
        </Typography>

        <DataTable
          title="Validated Source Accounts"
          columns={[
            { key: "accountNumber", label: "Account Number", width: 200 },
            { key: "ifsc", label: "IFSC", width: 160 },
            { key: "beneficiary_name", label: "Account Name", width: 240 },
          ]}
          rows={toValidatedRows(latest)}
          page={vPage}
          rowsPerPage={vRpp}
          onPageChange={(e, p) => setVPage(p)}
          onRowsPerPageChange={(e) => {
            setVRpp(parseInt(e.target.value, 10));
            setVPage(0);
          }}
          sx={{ width: "60%" }}
          emptyMessage={
            loading
              ? "Loading…"
              : "Please validate your source account to get started."
          }
        />

        <div style={{ marginTop: "15px" }}></div>

        <DataTable
          title="Money Load Accepted"
          columns={[
            { key: "date", label: "Date", width: 190 },
            { key: "amount", label: "Amount", width: 120 },
            {
              key: "sourceAccountName",
              label: "Source account name",
              width: 220,
            },
            { key: "accountNumber", label: "Account number", width: 180 },
            { key: "status", label: "Status", select: true, width: 260 },
            {
              key: "reason",
              label: "Reason",
              width: 320,
              renderCell: (_, value) => value || "Money load accepted",
            },
            { key: "utr", label: "UTR", width: 180 },
          ]}
          rows={toValidatedRowsAccepted(acceptedLoad)}
          page={aPage}
          rowsPerPage={aRpp}
          onPageChange={(e, p) => setAPage(p)}
          onRowsPerPageChange={(e) => {
            setARpp(parseInt(e.target.value, 10));
            setAPage(0);
          }}
          sx={{ width: "100%" }}
          emptyMessage="No accepted transactions yet."
        />

        <div style={{ marginTop: "15px" }}></div>

        <DataTable
          title="Money Load Rejected"
          columns={[
            { key: "date", label: "Date", width: 190 },
            { key: "amount", label: "Amount", width: 120 },
            {
              key: "sourceAccountName",
              label: "Source account name",
              width: 220,
            },
            { key: "accountNumber", label: "Account number", width: 180 },
            { key: "status", label: "Status", select: true, width: 150 },
            { key: "reason", label: "Reason", width: 360 },
            { key: "utr", label: "UTR", width: 180 },
          ]}
          rows={toValidatedRowsRejected(rejectedLoad)}
          page={rPage}
          rowsPerPage={rRpp}
          onPageChange={(e, p) => setRPage(p)}
          onRowsPerPageChange={(e) => {
            setRRpp(parseInt(e.target.value, 10));
            setRPage(0);
          }}
          sx={{ width: "100%" }}
          emptyMessage="No rejected transactions yet."
        />
      </Box>
    </>
  );
}

/** --- Helpers --- **/

function DetailRow({ label, value }) {
  return (
    <Grid2 item xs={12} sm={12} md={12}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "120px 8px 1fr", sm: "150px 8px 1fr" },
          alignItems: "baseline",
          columnGap: 1,
          py: 0.25,
        }}
      >
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
        >
          {label}
        </Typography>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          :
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 400 }}>
          {value}
        </Typography>
      </Box>
    </Grid2>
  );
}

function SectionHeader({ title, sx }) {
  return (
    <Box sx={{ ...sx, borderBottom: 1, borderColor: "divider", mb: 1.5, pb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
    </Box>
  );
}

/* ------------------------------------------------------------------
 * DataTable — DataGrid version.
 * Must stay at module scope: defining it inside ValidateBankAc would
 * remount it on every parent render, wiping its filter state and
 * stealing focus from the search box on each keystroke.
 *
 * Column defs are { key, label } plus three optional extras:
 *   width       -> column width (default 180)
 *   select      -> filter by value dropdown instead of a text box
 *   renderCell  -> (column, value) => node
 *
 * Optional props:
 *   panelWidth  -> width of the filter/columns popper (default 320)
 *   slotProps   -> per-table overrides, merged into the defaults below
 * ------------------------------------------------------------------ */
function DataTable({
  title,
  subtitle,
  headerRight, // optional: actions (buttons, chips, etc.)
  columns,
  rows,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  emptyMessage = "No data",
  renderCell,
  panelWidth = PANEL_WIDTH,
  slotProps: slotPropsOverride,
  sx,
}) {
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = React.useState("");
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [filterModel, setFilterModel] = React.useState(EMPTY_FILTER_MODEL);
  const [panelAnchorEl, setPanelAnchorEl] = React.useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({});
  // The columns panel always anchors here, whatever opened it.
  const columnsButtonRef = React.useRef(null);

  // Search -> MUI quick filter. The grid owns the filtering; this just
  // hands it the parsed values, debounced like the built-in quick filter.
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      apiRef.current?.setQuickFilterValues(parseQuickFilter(searchText.trim()));
      onPageChange?.(null, 0);
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchText]);

  const toggleSearch = () => {
    setSearchOpen((open) => {
      if (open) {
        setSearchText(""); // closing clears the quick filter
      }
      return !open;
    });
  };

  const openFilterPanel = (event) => {
    setPanelAnchorEl(event.currentTarget);
    apiRef.current?.showFilterPanel();
  };

  const openColumnsPanel = (event) => {
    // Pinned to the toolbar icon so the panel lands in the same place whether
    // it was opened from the toolbar or from the all-columns-hidden state.
    setPanelAnchorEl(columnsButtonRef.current ?? event?.currentTarget ?? null);
    apiRef.current?.showPreferences(GridPreferencePanelsValue.columns);
  };

  // rows need a stable id; the source arrays don't carry one
  const gridRows = React.useMemo(
    () =>
      (Array.isArray(rows) ? rows : []).map((row, index) => ({
        ...row,
        id: index,
      })),
    [rows],
  );

  const ellipsisCell = (value) => (
    <Tooltip title={value == null ? "" : String(value)}>
      <span
        style={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontFamily: SANSOURCE,
        }}
      >
        {value ?? "—"}
      </span>
    </Tooltip>
  );

  const gridColumns = React.useMemo(
    () =>
      (columns || []).map((column) => {
        const base = {
          field: column.key,
          headerName: column.label,
          width: column.width ?? 180,
          renderCell: (params) => {
            // column-level renderCell wins, then the table-level one,
            // then the plain value
            if (column.renderCell) {
              return ellipsisCell(column.renderCell(column, params.value));
            }
            if (renderCell) {
              return ellipsisCell(renderCell(column, params.value));
            }
            return ellipsisCell(params.value);
          },
        };

        if (column.select) {
          return {
            ...base,
            type: "singleSelect",
            valueOptions: [
              ...new Set(
                gridRows
                  .map((row) => row[column.key])
                  .filter((value) => value != null && value !== ""),
              ),
            ].sort(),
            filterOperators: IS_ONLY,
          };
        }

        return { ...base, filterOperators: CONTAINS_ONLY };
      }),
    [columns, gridRows, renderCell],
  );

  /* ----------------------------------------------------------------
   * The grid has no "no visible columns" state of its own: hide every
   * column and it renders an empty shell with no overlay. Detect it here
   * and put a Manage Columns action in that space instead.
   * ---------------------------------------------------------------- */
  const allColumnsHidden = React.useMemo(
    () =>
      gridColumns.length > 0 &&
      gridColumns.every((col) => columnVisibilityModel[col.field] === false),
    [gridColumns, columnVisibilityModel],
  );

  /* ----------------------------------------------------------------
   * Panel sizing.
   *
   * The panel used to inherit its width from the fixed minWidths on the
   * column/value inputs (160 + 220 + gaps + delete icon), which is wider
   * than a 60%-width table can hold — so panelContent grew a horizontal
   * scrollbar and the value box got cut off.
   *
   * Now the paper owns the width and the form inside stacks to fit it:
   * Column on one line, Value on the next. Nothing can overflow
   * horizontally regardless of how narrow the table is.
   *
   * `container: document.body` keeps the popper out of the two
   * `overflow: hidden` wrappers below, so the card's rounded corners
   * can't clip it either.
   *
   * PANEL_SX comes first in the sx array (font, colour, radius); the
   * sizing rules follow so they win where the two overlap.
   *
   * baseSelect covers the Column / Value dropdown menus, which open in
   * their own Popover outside the panel — see SELECT_MENU_SX.
   * ---------------------------------------------------------------- */
  const defaultSlotProps = React.useMemo(
    () => ({
      baseSelect: {
        MenuProps: {
          PaperProps: { sx: SELECT_MENU_SX },
        },
      },
      panel: {
        anchorEl: panelAnchorEl,
        placement: "bottom-end",
        container: typeof document !== "undefined" ? document.body : undefined,
        sx: [
          PANEL_SX,
          {
            zIndex: (theme) => theme.zIndex.modal,
            "& .MuiDataGrid-paper": {
              width: panelWidth,
              minWidth: 0,
              maxWidth: "calc(100vw - 32px)",
              borderRadius: "var(--br-xs)",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              boxShadow: "0 8px 24px rgba(76, 78, 100, 0.12)",
            },
            "& .MuiDataGrid-panelWrapper": { width: "100%", minWidth: 0 },
            "& .MuiDataGrid-panelContent": {
              width: "100%",
              minWidth: 0,
              overflowX: "hidden",
            },
            "& .MuiDataGrid-panelFooter": { p: 1 },
          },
        ],
      },
      filterPanel: {
        // one operator per column, so the selector is noise
        logicOperators: [],
        sx: {
          width: "100%",
          minWidth: 0,
          "& .MuiDataGrid-filterForm": {
            width: "100%",
            boxSizing: "border-box",
            p: 1.5,
            gap: 1,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
          },
          "& .MuiDataGrid-filterFormLogicOperatorInput": { display: "none" },
          "& .MuiDataGrid-filterFormOperatorInput": { display: "none" },
          // each input takes a full row inside the panel
          "& .MuiDataGrid-filterFormColumnInput": {
            flex: "1 1 100%",
            width: "auto",
            minWidth: 0,
          },
          "& .MuiDataGrid-filterFormValueInput": {
            flex: "1 1 100%",
            width: "auto",
            minWidth: 0,
          },
          "& .MuiDataGrid-filterFormDeleteIcon": {
            flex: "0 0 auto",
            alignSelf: "center",
            ml: "auto",
            mt: 0,
          },
          "& .MuiOutlinedInput-root": { borderRadius: "10px" },
        },
        filterFormProps: {
          logicOperatorInputProps: { sx: { display: "none" } },
          operatorInputProps: { sx: { display: "none" } },
          columnInputProps: {
            variant: "outlined",
            size: "small",
            sx: { width: "100%", minWidth: 0 },
          },
          valueInputProps: {
            variant: "outlined",
            size: "small",
            InputComponentProps: {
              variant: "outlined",
              size: "small",
              sx: { width: "100%", minWidth: 0 },
            },
          },
        },
      },
    }),
    [panelAnchorEl, panelWidth],
  );

  /* One level of merging so a call site can override a single slot without
   * dropping anchorEl or the sizing rules. sx is flattened into an array —
   * MUI merges those, whereas a plain spread would replace it wholesale. */
  const mergedSlotProps = React.useMemo(() => {
    const { panel, filterPanel, baseSelect, ...rest } = slotPropsOverride || {};

    return {
      ...rest,
      baseSelect: {
        ...defaultSlotProps.baseSelect,
        ...baseSelect,
        MenuProps: {
          ...defaultSlotProps.baseSelect.MenuProps,
          ...baseSelect?.MenuProps,
          PaperProps: {
            ...defaultSlotProps.baseSelect.MenuProps.PaperProps,
            ...baseSelect?.MenuProps?.PaperProps,
          },
        },
      },
      panel: {
        ...defaultSlotProps.panel,
        ...panel,
        sx: [defaultSlotProps.panel.sx, panel?.sx].flat().filter(Boolean),
      },
      filterPanel: {
        ...defaultSlotProps.filterPanel,
        ...filterPanel,
        sx: [defaultSlotProps.filterPanel.sx, filterPanel?.sx]
          .flat()
          .filter(Boolean),
        filterFormProps: {
          ...defaultSlotProps.filterPanel.filterFormProps,
          ...filterPanel?.filterFormProps,
        },
      },
    };
  }, [defaultSlotProps, slotPropsOverride]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        minWidth: 0,
        maxWidth: "100%",
        fontFamily: SANSOURCE,
        ...sx,
      }}
    >
      {/* Attached header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box>
          {title && (
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                fontFamily: SANSOURCE,
                color: TEXT_COLOR,
              }}
            >
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
                fontFamily: SANSOURCE,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {headerRight || null}

          {/* search — hidden until the search button is pressed */}
          {searchOpen && (
            <TextField
              size="small"
              autoFocus
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") toggleSearch();
              }}
              sx={{
                minWidth: 200,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                "& .MuiInputBase-input": {
                  fontFamily: SANSOURCE,
                  fontSize: 14,
                  color: TEXT_COLOR,
                },
                "& .MuiInputBase-input::placeholder": {
                  fontFamily: SANSOURCE,
                  color: MUTED_COLOR,
                  opacity: 1,
                },
              }}
            />
          )}

          <Tooltip title="Search">
            <IconButton size="small" onClick={toggleSearch}>
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Filter">
            <IconButton size="small" onClick={openFilterPanel}>
              <FilterListIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Columns">
            <IconButton
              ref={columnsButtonRef}
              size="small"
              onClick={openColumnsPanel}
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={gridRows}
          columns={gridColumns}
          autoHeight
          rowHeight={52}
          disableColumnMenu
          disableRowSelectionOnClick
          filterModel={filterModel}
          onFilterModelChange={(model) => {
            setFilterModel(model);
            onPageChange?.(null, 0);
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          paginationModel={{ page: page ?? 0, pageSize: rowsPerPage ?? 5 }}
          onPaginationModelChange={(model) => {
            if (model.pageSize !== rowsPerPage) {
              onRowsPerPageChange?.({ target: { value: model.pageSize } });
            }
            if (model.page !== page) {
              onPageChange?.(null, model.page);
            }
          }}
          pageSizeOptions={[5, 10, 20]}
          slotProps={mergedSlotProps}
          localeText={{
            noRowsLabel: emptyMessage,
            noResultsOverlayLabel: "No rows match this search or filter.",
            filterPanelColumns: "Column",
            filterPanelInputLabel: "Value",
            filterPanelInputPlaceholder: "Filter value",
          }}
          sx={{
            width: "100%",
            maxWidth: "100%",
            border: "none",
            fontFamily: SANSOURCE,
            color: TEXT_COLOR,
            // keeps room for the overlay when every column is hidden
            ...(allColumnsHidden && { minHeight: 280 }),

            // Source Sans through every part of the grid, including the
            // nodes rendered by renderCell
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cell, & .MuiDataGrid-cell *, & .MuiDataGrid-overlay, & .MuiDataGrid-footerContainer, & .MuiTablePagination-root":
              {
                fontFamily: SANSOURCE,
              },

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f7f8fa",
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
              fontSize: 13,
              color: HEADER_COLOR,
            },
            "& .MuiDataGrid-columnHeader": {
              paddingLeft: "10px",
              paddingRight: 0,
            },
            "& .MuiDataGrid-columnSeparator": { display: "none" },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              borderBottom: "none",
              overflow: "hidden",
              paddingLeft: "10px",
              paddingRight: 0,
              fontSize: 14,
            },
            "& .MuiDataGrid-footerContainer": { borderTop: "none" },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
          }}
        />

        {/* every column hidden — the grid draws nothing, so offer the
            way back instead of a blank table */}
        {allColumnsHidden && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              backgroundColor: "#fff",
              zIndex: 2,
            }}
          >
            <span
              style={{
                fontSize: 14,
                color: "#667085",
                fontFamily: SANSOURCE,
              }}
            >
              All columns are hidden.
            </span>
            <Button
              variant="contained"
              size="small"
              onClick={openColumnsPanel}
              sx={{
                bgcolor: "#2348AB",
                "&:hover": { bgcolor: "#3f55c7" },
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
              }}
            >
              Manage Columns
            </Button>
          </div>
        )}
      </Box>
    </Box>
  );
}
